import { useQuery } from '@tanstack/react-query';
import { Plus, X } from 'lucide-react';
import {
  CONSENT_CHANNEL_LABELS,
  CONTACT_CLASSIFICATIONS,
  CONTACT_CLASSIFICATION_LABELS,
  CONTACT_CHANNEL_KINDS,
  CONTACT_CHANNEL_KIND_LABELS,
  CONTACT_KINDS,
  CONTACT_KIND_LABELS,
  SEGMENT_CONSENT_CHANNELS,
  SEGMENT_FIELDS,
  SEGMENT_FIELD_LABELS,
  segmentInputSchema,
  type Segment,
  type SegmentCondition,
  type SegmentField,
  type SegmentInput,
} from '@aimmo/contracts';
import {
  Badge,
  Button,
  defineResource,
  Input,
  NativeSelect,
  tsRestResourceClient,
  useDebouncedValue,
} from '@aimmo/ui';
import { api, assertOk } from '../lib/api.js';
import { queryKeys } from '../lib/queries.js';
import { segmentPreviewBody } from '../lib/segments.js';

/** The one condition each field starts as when it is picked. */
function emptyCondition(field: SegmentField): SegmentCondition {
  switch (field) {
    case 'kind':
      return { field, value: 'person' };
    case 'classification':
      return { field, value: 'lead' };
    case 'comune_id':
      return { field, values: [] as unknown as [string, ...string[]] };
    case 'has_channel':
      return { field, value: 'email' };
    case 'consent':
      return { field, value: 'email' };
    default:
      return { field, value: new Date().toISOString() };
  }
}

function conditionLabel(condition: SegmentCondition): string {
  const name = SEGMENT_FIELD_LABELS[condition.field];
  switch (condition.field) {
    case 'kind':
      return `${name}: ${CONTACT_KIND_LABELS[condition.value]}`;
    case 'classification':
      return `${name}: ${CONTACT_CLASSIFICATION_LABELS[condition.value]}`;
    case 'comune_id':
      return `${name}: ${condition.values.length}`;
    case 'has_channel':
      return `${name}: ${CONTACT_CHANNEL_KIND_LABELS[condition.value]}`;
    case 'consent':
      return `${name}: ${CONSENT_CHANNEL_LABELS[condition.value]}`;
    default:
      return `${name}: ${condition.value.slice(0, 10)}`;
  }
}

/** The value control each field needs, and nothing more general than that. */
function ValueField({
  condition,
  disabled,
  onChange,
}: {
  condition: SegmentCondition;
  disabled: boolean;
  onChange: (next: SegmentCondition) => void;
}) {
  const select = (options: readonly string[], labels: Record<string, string>, value: string) => (
    <NativeSelect
      value={value}
      disabled={disabled}
      onChange={(event) =>
        onChange({ ...condition, value: event.target.value } as SegmentCondition)
      }
    >
      {options.map((option) => (
        <option key={option} value={option}>
          {labels[option] ?? option}
        </option>
      ))}
    </NativeSelect>
  );

  switch (condition.field) {
    case 'kind':
      return select(CONTACT_KINDS, CONTACT_KIND_LABELS, condition.value);
    case 'classification':
      return select(CONTACT_CLASSIFICATIONS, CONTACT_CLASSIFICATION_LABELS, condition.value);
    case 'has_channel':
      return select(CONTACT_CHANNEL_KINDS, CONTACT_CHANNEL_KIND_LABELS, condition.value);
    case 'consent':
      return select(SEGMENT_CONSENT_CHANNELS, CONSENT_CHANNEL_LABELS, condition.value);
    case 'comune_id':
      // Ids, comma separated. A picker over 7,900 comuni is its own piece of
      // work; until it exists, saying so is better than a text box that looks
      // like it takes names.
      return (
        <Input
          value={condition.values.join(',')}
          disabled={disabled}
          placeholder="id comune, separati da virgola"
          onChange={(event) =>
            onChange({
              field: 'comune_id',
              values: event.target.value
                .split(',')
                .map((value) => value.trim())
                .filter(Boolean) as unknown as [string, ...string[]],
            })
          }
        />
      );
    default:
      return (
        <Input
          type="date"
          value={condition.value.slice(0, 10)}
          disabled={disabled}
          onChange={(event) =>
            onChange({
              field: condition.field,
              value: new Date(`${event.target.value}T00:00:00.000Z`).toISOString(),
            })
          }
        />
      );
  }
}

/**
 * The audience editor (M9 / P2).
 *
 * A row per condition, all of which must hold. There is no free text that could
 * be mistaken for a query language: the field is a closed list and the value is
 * whatever control that field needs, because the API translates conditions
 * through an allow-list and can express nothing else.
 */
function ConditionBuilder({
  value,
  onChange,
  onBlur,
  disabled,
}: {
  value: unknown;
  onChange: (value: unknown) => void;
  onBlur: () => void;
  disabled: boolean;
}) {
  const definition =
    typeof value === 'object' && value !== null && 'conditions' in value
      ? (value as { conditions: SegmentCondition[] })
      : { conditions: [] };
  const conditions = definition.conditions;

  const write = (next: SegmentCondition[]) => {
    onChange({ conditions: next });
    onBlur();
  };

  // The live count, asked of the API rather than guessed at: it is the one
  // number that tells somebody whether the audience they just described is the
  // one they meant. Only asked once the definition would validate.
  //
  // Asked once the editing pauses: a date typed digit by digit, or a comune
  // being picked, is otherwise a count per keystroke.
  const settled = useDebouncedValue(JSON.stringify(conditions), 400);
  const previewBody = segmentPreviewBody(settled);
  const preview = useQuery({
    queryKey: queryKeys.segments.preview(settled),
    enabled: previewBody !== null,
    queryFn: async ({ signal }) => {
      if (previewBody === null) return null;
      const result = await api.campaigns.previewSegment({
        body: previewBody,
        fetchOptions: { signal },
      });
      // A definition still being written (a comune not yet picked) is refused
      // with a 400: that is "no count yet", not a failure. Anything else is.
      if (result.status === 400) return null;
      return assertOk(result).body.total;
    },
  });

  return (
    <div className="grid gap-2">
      {conditions.map((condition, index) => (
        <div key={`${condition.field}-${index}`} className="flex flex-wrap items-center gap-2">
          <NativeSelect
            value={condition.field}
            disabled={disabled}
            aria-label="Campo"
            onChange={(event) =>
              write(
                conditions.map((current, position) =>
                  position === index ? emptyCondition(event.target.value as SegmentField) : current,
                ),
              )
            }
          >
            {SEGMENT_FIELDS.map((field) => (
              <option key={field} value={field}>
                {SEGMENT_FIELD_LABELS[field]}
              </option>
            ))}
          </NativeSelect>

          <ValueField
            condition={condition}
            disabled={disabled}
            onChange={(next) =>
              write(conditions.map((current, position) => (position === index ? next : current)))
            }
          />

          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label="Togli la condizione"
            disabled={disabled}
            onClick={() => write(conditions.filter((_, position) => position !== index))}
          >
            <X />
          </Button>
        </div>
      ))}

      <div className="flex items-center gap-3">
        <Button
          type="button"
          variant="secondary"
          size="sm"
          disabled={disabled || conditions.length >= 20}
          onClick={() => write([...conditions, emptyCondition('kind')])}
        >
          <Plus /> Aggiungi condizione
        </Button>

        {conditions.length === 0 ? (
          // Refused by the schema too. An empty definition is not "everybody":
          // that is how somebody writes to every contact they hold by leaving a
          // form untouched.
          <span className="text-body text-muted">
            Serve almeno una condizione: un segmento vuoto non è «tutti».
          </span>
        ) : (
          <span className="text-body text-muted">
            {preview.isPending
              ? 'Conteggio…'
              : preview.data === null || preview.data === undefined
                ? 'Conteggio non disponibile'
                : `${preview.data} contatti`}
          </span>
        )}
      </div>
    </div>
  );
}

export const segmentsResource = defineResource<Segment, SegmentInput, SegmentInput, SegmentInput>({
  key: 'segments',
  label: { one: 'Segmento', many: 'Segmenti', gender: 'm' },
  client: tsRestResourceClient({
    list: api.campaigns.listSegments,
    get: api.campaigns.getSegment,
    create: api.campaigns.createSegment,
    update: api.campaigns.updateSegment,
    remove: api.campaigns.removeSegment,
  }),
  getRowId: (row) => row.id,
  defaultState: { sort: 'name', order: 'asc' },
  search: { placeholder: 'Cerca per nome…' },
  columns: [
    { id: 'name', header: 'Nome', sortKey: 'name' },
    {
      id: 'conditions',
      header: 'Condizioni',
      cell: ({ row }) => (
        <div className="flex flex-wrap gap-1">
          {row.definition.conditions.map((condition, index) => (
            <Badge key={index} tone="neutral">
              {conditionLabel(condition)}
            </Badge>
          ))}
        </div>
      ),
    },
  ],
  form: {
    schema: segmentInputSchema,
    defaultValues: { name: '', definition: { conditions: [] } },
    sections: [
      {
        id: 'main',
        title: 'Segmento',
        fields: [
          { name: 'name', label: 'Nome', type: 'text', required: true },
          {
            name: 'definition',
            label: 'Condizioni',
            type: 'custom',
            render: ({ value, onChange, onBlur, disabled }) => (
              <ConditionBuilder
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                disabled={disabled}
              />
            ),
          },
        ],
      },
    ],
  },
  detail: {
    title: (row) => row.name,
    sections: [
      {
        id: 'main',
        title: 'Segmento',
        fields: [
          { id: 'name', label: 'Nome' },
          {
            id: 'definition',
            label: 'Condizioni',
            render: (row) => row.definition.conditions.map(conditionLabel).join(' · '),
          },
        ],
      },
    ],
  },
});
